import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import {
  User,
  Mail,
  Building,
  Bell,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import ProfilePhoto from '../components/ProfilePhoto';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications'>('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profileData, setProfileData] = useState({
    firstName: user?.given_name || '',
    lastName: user?.family_name || '',
    email: user?.email || '',
    company: user?.['custom:company'] || user?.['custom:organization'] || '',
    title: user?.['custom:title'] || '',
    phone: user?.['custom:phone'] || user?.phone_number || '',
  });

  // Load user data from Cognito attributes
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.given_name || '',
        lastName: user.family_name || '',
        email: user.email || '',
        company: user['custom:company'] || user['custom:organization'] || '',
        title: user['custom:title'] || '',
        phone: user['custom:phone'] || user.phone_number || '',
      });
    }
  }, [user]);

  // Format phone number to (###) ###-#### format
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setProfileData({ ...profileData, phone: formattedPhone });
  };

  const [notificationSettings, setNotificationSettings] = useState({
    niwtQueryNotifications: localStorage.getItem('niwtQueryNotifications') !== 'false',
    activityDigest: localStorage.getItem('activityDigest') || 'off',
    hideWelcomeBoxOnLogin: localStorage.getItem('hideWelcomeBoxOnLogin') === 'true',
  });

  const handleProfileSave = async () => {
    setLoading(true);
    setMessage(null);

    try {
      // Update Cognito user attributes
      await authService.updateUserAttributes({
        'given_name': profileData.firstName,
        'family_name': profileData.lastName,
        'custom:company': profileData.company,
        'custom:title': profileData.title,
        'custom:phone': profileData.phone,
      });

      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSave = async () => {
    setLoading(true);
    setMessage(null);

    try {
      localStorage.setItem('niwtQueryNotifications', notificationSettings.niwtQueryNotifications ? 'true' : 'false');
      localStorage.setItem('activityDigest', notificationSettings.activityDigest);
      localStorage.setItem('hideWelcomeBoxOnLogin', notificationSettings.hideWelcomeBoxOnLogin ? 'true' : 'false');
      if (!notificationSettings.hideWelcomeBoxOnLogin) {
        localStorage.removeItem('hideWelcomeBox');
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setMessage({ type: 'success', text: 'Notification preferences saved' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save notification preferences' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-libre">Account Settings</h1>
        <p className="text-gray-600 mt-1">Manage your profile and preferences</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`flex items-center p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'profile'
                ? 'border-niwt-primary text-niwt-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'notifications'
                ? 'border-niwt-primary text-niwt-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Notifications
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Photo Section */}
            <div className="flex items-center space-x-6 pb-6 border-b border-gray-200">
              <ProfilePhoto
                userName={`${profileData.firstName} ${profileData.lastName}`}
                userEmail={profileData.email}
                size="large"
                editable={true}
                onPhotoChange={(photoUrl) => {
                  setMessage({ type: 'success', text: 'Profile photo updated successfully' });
                  window.dispatchEvent(new Event('storage'));
                }}
              />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Profile Photo</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Upload a photo to personalize your account
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  JPG, PNG or GIF. Max size 5MB.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
              />
              <p className="mt-1 text-sm text-gray-500">Email cannot be changed</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-1" />
                  Company
                </label>
                <input
                  type="text"
                  value={profileData.company}
                  onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  value={profileData.title}
                  onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                  placeholder="e.g., Chief Technology Officer"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={handlePhoneChange}
                className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                placeholder="(555) 123-4567"
                maxLength={14}
              />
              <p className="mt-1 text-xs text-gray-500">Format: (###) ###-####</p>
            </div>

            <button
              onClick={handleProfileSave}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-niwt-primary text-white rounded-lg hover:bg-niwt-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Niwt Query Notifications</h3>
                  <p className="text-sm text-gray-600 mt-1">Get notified when someone asks your Niwt a question</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.niwtQueryNotifications}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, niwtQueryNotifications: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-niwt-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-niwt-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Activity Digest</h3>
                  <p className="text-sm text-gray-600 mt-1">Periodic summary of your Niwt's activity</p>
                </div>
                <select
                  value={notificationSettings.activityDigest}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, activityDigest: e.target.value })}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent"
                >
                  <option value="off">Off</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Hide Welcome Message</h3>
                  <p className="text-sm text-gray-600 mt-1">Don't show welcome message on dashboard after login</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.hideWelcomeBoxOnLogin}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, hideWelcomeBoxOnLogin: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-niwt-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-niwt-primary"></div>
                </label>
              </div>
            </div>

            <button
              onClick={handleNotificationSave}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-niwt-primary text-white rounded-lg hover:bg-niwt-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
